import CustomPagination from '@/platform/common/components/CustomPagination/CustomPagination';
import NoDataRow from '@/platform/common/components/NoDataRow/NoDataRow';
import SearchBarFilter from '@/platform/common/components/SearchBarFilter/SearchBarFilter';
import { useFilters } from '@/platform/hooks/useFilters';
import { useConfirmationStore } from '@/platform/stores/useConfirmationStore';
import {
    Button,
    Container,
    Menu,
    MenuItem,
    MenuItems,
    MenuSeparator,
    Segment,
    SegmentContent,
    SegmentFooter,
    SegmentHeader,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
} from '@/platform/ui';
import { MenuButton } from '@headlessui/react';
import {
    EllipsisVerticalIcon,
    PencilIcon,
    PlusIcon,
    TrashIcon,
} from '@heroicons/react/24/outline';
import { Link, router, usePage } from '@inertiajs/react';

const IndexView = () => {
    const { auth, objection_contents } = usePage().props;
    const { updateFilters, isSorted } = useFilters(['objection_contents']);
    const confirm = useConfirmationStore(state => state.confirm);

    const confirmDelete = objectionContent =>
        confirm({
            title: 'Bezwaar verwijderen?',
            description:
                'Weet je zeker dat je dit bezwaar wilt verwijderen? Alle gekoppelde bezwaarschriften worden ook geleegd.',
            confirmLabel: 'Verwijderen',
            onConfirm: close => {
                destroy(objectionContent);
                close();
            },
        });

    const destroy = objectionContent => {
        router.delete(`/objection-content/${objectionContent.id}`, {
            preserveScroll: true,
            preserveState: true,
        });
    };

    return (
        <Container>
            <Segment>
                <SegmentHeader
                    header={`Bezwaren (${objection_contents?.meta?.total || 0})`}
                >
                    {auth?.permissions?.includes(
                        'create objection contents'
                    ) && (
                        <Button
                            variant='primary'
                            as={Link}
                            href='/objection-content/create'
                        >
                            <PlusIcon className='size-5' />
                            Nieuw bezwaar
                        </Button>
                    )}
                </SegmentHeader>
                <SegmentContent>
                    <SearchBarFilter only={['objection_contents']} />

                    <Table striped>
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell
                                    sorted={isSorted('name')}
                                    onClick={() =>
                                        updateFilters('sort', 'name')
                                    }
                                >
                                    Naam
                                </TableHeaderCell>
                                <TableHeaderCell
                                    className='w-px'
                                    textAlign='right'
                                />
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {objection_contents?.meta?.total === 0 && (
                                <NoDataRow numberOfColumns={2} />
                            )}

                            {objection_contents?.data?.map(objectionContent => (
                                <TableRow key={objectionContent.id}>
                                    <TableCell>
                                        {auth?.permissions?.includes(
                                            'update objection contents'
                                        ) ? (
                                            <Link
                                                href={`/objection-content/${objectionContent.id}/edit`}
                                            >
                                                {objectionContent.name}
                                            </Link>
                                        ) : (
                                            objectionContent.name
                                        )}
                                    </TableCell>
                                    <TableCell
                                        className='w-px'
                                        singleLine
                                        textAlign='right'
                                    >
                                        {[
                                            'update objection contents',
                                            'delete objection contents',
                                        ]?.some(permission =>
                                            auth?.permissions?.includes(
                                                permission
                                            )
                                        ) && (
                                            <Menu>
                                                <MenuButton>
                                                    <EllipsisVerticalIcon className='size-5' />
                                                </MenuButton>
                                                <MenuItems anchor='bottom end'>
                                                    {auth?.permissions?.includes(
                                                        'update objection contents'
                                                    ) && (
                                                        <>
                                                            <MenuItem
                                                                as={Link}
                                                                href={`/objection-content/${objectionContent.id}/edit`}
                                                                icon={
                                                                    PencilIcon
                                                                }
                                                            >
                                                                Wijzigen
                                                            </MenuItem>

                                                            <MenuSeparator />
                                                        </>
                                                    )}
                                                    {auth?.permissions?.includes(
                                                        'delete objection contents'
                                                    ) && (
                                                        <MenuItem
                                                            className='text-red-600'
                                                            onClick={() =>
                                                                confirmDelete(
                                                                    objectionContent
                                                                )
                                                            }
                                                            icon={TrashIcon}
                                                        >
                                                            Verwijderen
                                                        </MenuItem>
                                                    )}
                                                </MenuItems>
                                            </Menu>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </SegmentContent>
                <SegmentFooter>
                    <CustomPagination
                        meta={objection_contents?.meta}
                        only={['objection_contents']}
                    />
                </SegmentFooter>
            </Segment>
        </Container>
    );
};

export default IndexView;
